import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@nomoh-monorepo/data-access-auth';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { PessoaDataService } from '@nomoh-monorepo/data-access-shared-services';
import { environment } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IPessoa,
  IPlano,
  SistemaEnum,
  StatusHistorico,
} from '@nomoh-monorepo/shared-types';
import { LuxonNomoh } from '@nomoh-monorepo/shared-util-luxon-nomoh';
import {
  formatarCEP,
  formatarCNPJ,
  formatarCPF,
  formatarTelefone,
} from '@nomoh-monorepo/shared-util-tools';
import { DateTime } from 'luxon';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { CompletarCadastroModalComponent } from '../completar-cadastro-modal/completar-cadastro-modal.component';
import { RenovarPlanoModalComponent } from '../renovar-plano-modal/renovar-plano-modal.component';
import { RedefinirSenhaContaComponent } from './redefinir-senha-conta/redefinir-senha-conta.component';

@Component({
  selector: 'lib-pessoa-minha-conta',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    AvatarModule,
    ProgressSpinnerModule,
    TooltipModule,
    TranslateModule,
    RenovarPlanoModalComponent,
    RedefinirSenhaContaComponent,
    CompletarCadastroModalComponent,
  ],
  templateUrl: './pessoa-minha-conta.component.html',
  styleUrls: ['./pessoa-minha-conta.component.scss'],
})
export class PessoaMinhaContaComponent implements OnInit, OnChanges, OnDestroy {
  // Input caso usado contextualmente por uma rota
  @Input() pessoaId!: string;
  @Input() exibirAjudaSuporte = true;
  @Input() exibirMeusSistemas: boolean | null = null;
  @Input() imagemCapa?: string | null;

  // Compara hostname do app atual com o configurado no environment (ignora http/https e www).
  private isMesmoHostFrontend(configuredUrl: string): boolean {
    if (typeof window === 'undefined' || !configuredUrl) {
      return false;
    }

    try {
      const configured = new URL(configuredUrl);
      const atual = window.location;
      const normalizarHost = (host: string) =>
        host.replace(/^www\./i, '').toLowerCase();

      if (
        normalizarHost(configured.hostname) !== normalizarHost(atual.hostname)
      ) {
        return false;
      }

      if (configured.port && atual.port && configured.port !== atual.port) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  get isNomohClienteApp(): boolean {
    return this.isMesmoHostFrontend(environment.frontend?.nomohCliente ?? '');
  }

  private authService = inject(AuthService);

  // Computeds de Contexto Bardo
  $bardoContextoAtivo = computed(() => this.authService.$bardoContextoAtivo());
  $isProfissionalBardo = computed(
    () => this.$bardoContextoAtivo()?.tipo === 'PROFISSIONAL'
  );
  $isDonoBardo = computed(() => this.$bardoContextoAtivo()?.tipo === 'DONO');
  $nomeEstabelecimento = computed(
    () => this.$bardoContextoAtivo()?.nomeEstabelecimento ?? null
  );

  $papelFormatado = computed(() => {
    const ctx = this.$bardoContextoAtivo();
    if (!ctx) return null;
    if (ctx.tipo === 'DONO') return 'PESSOA_MINHA_CONTA.PAPEL.PROPRIETARIO';
    switch (ctx.papel) {
      case 'ADMINISTRADOR':
        return 'PESSOA_MINHA_CONTA.PAPEL.ADMINISTRADOR';
      case 'RECEPCAO':
        return 'PESSOA_MINHA_CONTA.PAPEL.RECEPCAO';
      case 'PROFISSIONAL':
        return 'PESSOA_MINHA_CONTA.PAPEL.PROFISSIONAL';
      default:
        return ctx.papel;
    }
  });

  get isDonoBardo(): boolean {
    return this.$isDonoBardo();
  }

  get mostrarMeusSistemas(): boolean {
    if (this.exibirMeusSistemas !== null) {
      return this.exibirMeusSistemas;
    }
    return this.isNomohClienteApp || this.isDonoBardo;
  }

  get mostrarAjudaSuporte(): boolean {
    if (!this.exibirAjudaSuporte) return false;
    return this.isNomohClienteApp || this.isDonoBardo || !!this.$bardoContextoAtivo();
  }

  // Eventos de navegação para fora do escopo (Edit/Access Systems)
  @Output() editarPerfil = new EventEmitter<void>(); // Mantido por retrocompatibilidade se for chamado num modal em algum lugar
  @Output() acessarNomoh = new EventEmitter<void>();
  @Output() acessarBardo = new EventEmitter<void>();

  private _pessoa = signal<IPessoa | null>(null);
  private _planos = signal<IPlano[]>([]);
  private _loading = signal<boolean>(false);

  $pessoa = this._pessoa.asReadonly();
  $planos = this._planos.asReadonly();
  $loading = this._loading.asReadonly();

  translate = inject(TranslateService);
  private pessoaDataService = inject(PessoaDataService);
  private notificacao = inject(NotificacaoService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  constructor() {
    effect(() => {
      this.sincronizarComUserInfo();
    });
  }

  private _pollingAtivo = false;

  $aguardandoAtivacao = signal(false);
  $avisoExpirado = signal(false);
  $avisoCancelado = signal(false);
  $sistemaExpiradoNome = signal('');
  $sistemaExpiradoId = signal<string | null>(null);
  $modalRenovarAberta = signal(false);
  $modalSenhaAberta = signal(false);
  private _loadingPortal = signal(false);
  $loadingPortal = this._loadingPortal.asReadonly();
  private _loadingBardo = signal(false);
  $loadingBardo = this._loadingBardo.asReadonly();

  // Computeds
  $imagemUrl = computed(() => {
    const pessoa = this.$pessoa();
    if (pessoa?.imagemPerfil && pessoa?.typeImage) {
      return `data:${pessoa.typeImage};base64,${pessoa.imagemPerfil}`;
    }
    return null;
  });

  $imagemCapaUrl = computed(() => {
    if (this.imagemCapa) return this.imagemCapa;
    const pessoa = this.$pessoa() as (IPessoa & { imagemCapa?: string; typeImageCapa?: string }) | null;
    if (pessoa?.imagemCapa && pessoa?.typeImageCapa) {
      return `data:${pessoa.typeImageCapa};base64,${pessoa.imagemCapa}`;
    }
    return null;
  });

  $temPlanoNomoh = computed(() => {
    return this.$planos().some((p) => p.sistema?.token === SistemaEnum.Nomoh);
  });

  $temPlanoBardo = computed(() => {
    return this.$planos().some((p) => p.sistema?.token === SistemaEnum.Bardo);
  });

  $planoNomohInfo = computed(() => {
    return this.$planos().find((p) => p.sistema?.token === SistemaEnum.Nomoh);
  });

  $planoBardoInfo = computed(() => {
    return this.$planos().find((p) => p.sistema?.token === SistemaEnum.Bardo);
  });

  $planoNomohStatus = computed(() => {
    return this.getPlanoStatus(this.$planoNomohInfo());
  });

  $planoBardoStatus = computed(() => {
    return this.getPlanoStatus(this.$planoBardoInfo());
  });

  $proximaCobrancaNomoh = computed(() =>
    this.getProximaCobranca(this.$planoNomohInfo())
  );
  $proximaCobrancaBardo = computed(() =>
    this.getProximaCobranca(this.$planoBardoInfo())
  );

  getPlanoStatus(plano?: IPlano) {
    if (!plano) return null;
    if (plano.subscriptionStatus === 'pending') {
      return {
        labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.AGUARDANDO_PAGAMENTO',
        severity: 'warning',
        blockAccess: true,
        isPending: true,
      };
    }
    if (
      plano.subscriptionStatus === 'cancelled' ||
      plano.subscriptionStatus === StatusHistorico.CANCELADO
    ) {
      return {
        labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.CANCELADO',
        severity: 'danger',
        blockAccess: true,
      };
    }
    if (
      plano.subscriptionStatus === 'expired' ||
      plano.subscriptionStatus === StatusHistorico.EXPIRADO
    ) {
      return {
        labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.EXPIRADO',
        severity: 'danger',
        blockAccess: true,
      };
    }
    if (!plano.subscriptionEndsAt) {
      return {
        labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.PLANO_VITALICIO',
        severity: 'success',
        blockAccess: false,
      };
    }

    if (LuxonNomoh.estaVencidoPorDataCivil(plano.subscriptionEndsAt)) {
      return {
        labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.EXPIRADO',
        severity: 'danger',
        blockAccess: true,
      };
    }

    const diffDays = LuxonNomoh.diasRestantesDataCivil(
      plano.subscriptionEndsAt as string
    );

    if (diffDays === 0) {
      return {
        labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.EXPIRA_HOJE',
        severity: 'warning',
        blockAccess: false,
      };
    }

    if (diffDays <= 5) {
      return {
        labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.EXPIRA_EM_DIAS',
        labelParams: { dias: diffDays },
        severity: 'warning',
        blockAccess: false,
      };
    }

    return {
      labelKey: 'PESSOA_MINHA_CONTA.STATUS_PLANO.VALIDO_ATE',
      labelParams: {
        data: DateTime.fromISO(plano.subscriptionEndsAt as string).toFormat(
          'dd/MM/yyyy'
        ),
      },
      severity: 'success',
      blockAccess: false,
    };
  }

  getProximaCobranca(plano?: IPlano): string | null {
    if (
      !plano ||
      plano.subscriptionStatus !== 'active' ||
      !plano.autoRenew ||
      !plano.subscriptionEndsAt
    ) {
      return null;
    }
    return DateTime.fromISO(plano.subscriptionEndsAt as string).toFormat(
      'dd/MM/yyyy'
    );
  }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;

    if (params.get('avisoExpirado') === 'true') {
      this.$avisoExpirado.set(true);
      this.$sistemaExpiradoNome.set(params.get('sistemaNome') ?? 'o sistema');
      this.$sistemaExpiradoId.set(params.get('sistemaId'));
    }

    if (params.get('avisoCancelado') === 'true') {
      this.$avisoCancelado.set(true);
      this.$sistemaExpiradoNome.set(params.get('sistemaNome') ?? 'o sistema');
      this.$sistemaExpiradoId.set(params.get('sistemaId'));
    }

    if (this.pessoaId) {
      if (params.get('pagamento') === 'sucesso') {
        this.aguardarAtivacaoPlano();
      } else {
        this.carregarDados();
      }
    }
  }

  ngOnDestroy(): void {
    this._pollingAtivo = false;
  }

  private async aguardarAtivacaoPlano(): Promise<void> {
    this._pollingAtivo = true;
    this.$aguardandoAtivacao.set(true);
    this._loading.set(true);

    const maxTentativas = 15;
    const intervaloMs = 2000;

    for (let i = 0; i < maxTentativas; i++) {
      if (!this._pollingAtivo) return;

      try {
        const [pessoa, planos] = await Promise.all([
          this.pessoaDataService.buscarPorId(this.pessoaId),
          this.pessoaDataService.buscarPlanos(this.pessoaId),
        ]);
        this._pessoa.set(pessoa);
        this._planos.set(planos);

        const algumAtivo = planos.some((p) => p.subscriptionStatus === 'active');
        if (algumAtivo) {
          this._loading.set(false);
          this.$aguardandoAtivacao.set(false);
          this._pollingAtivo = false;
          await this.authService.marcarPlanoAtivoAposPagamento();
          this.notificacao.notificacaoSuccess(
            'Pagamento realizado com sucesso! Seu plano já está ativo.'
          );
          await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { pagamento: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
          return;
        }
      } catch {
        // ignora e tenta novamente
      }

      if (i < maxTentativas - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervaloMs));
      }
    }

    // Timeout após 30s: mostra os dados atuais e avisa o usuário
    this._loading.set(false);
    this.$aguardandoAtivacao.set(false);
    this._pollingAtivo = false;
    this.notificacao.notificacaoWarn(
      'Pagamento recebido! A ativação pode levar alguns instantes. Recarregue a página em breve.'
    );
  }

  abrirModalRenovar(sistemaId: string | null) {
    if (!sistemaId) return;
    this.$sistemaExpiradoId.set(sistemaId);
    this.$modalRenovarAberta.set(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pessoaId']) {
      this.sincronizarComUserInfo();
      if (!changes['pessoaId'].firstChange && this.pessoaId) {
        this.carregarDados();
      }
    }
  }

  private sincronizarComUserInfo(): void {
    const userInfo = this.pessoaDataService.$userInfo();
    if (userInfo?.id && this.pessoaId && userInfo.id === this.pessoaId) {
      this._pessoa.set(userInfo);
    }
  }

  async carregarDados(): Promise<void> {
    this._loading.set(true);
    try {
      const [pessoa, planos] = await Promise.all([
        this.pessoaDataService.buscarPorId(this.pessoaId),
        this.pessoaDataService.buscarPlanos(this.pessoaId),
      ]);
      this._pessoa.set(pessoa);
      this._planos.set(planos);
    } finally {
      this._loading.set(false);
    }
  }

  // Formatting tools
  formatarTelefone(telefone: string | null | undefined): string {
    return telefone ? formatarTelefone(telefone) : '';
  }

  formatarCPFOUCNPJ(cpfcnpj: string): string {
    if (!cpfcnpj) return '';
    return cpfcnpj.length === 11 ? formatarCPF(cpfcnpj) : formatarCNPJ(cpfcnpj);
  }

  formatarCEP(cep: string | null | undefined): string {
    return cep ? formatarCEP(cep) : '';
  }

  formatarData(dataISO: string): string {
    if (!dataISO) return '';
    return DateTime.fromISO(dataISO)
      .toLocal()
      .toLocaleString(DateTime.DATE_SHORT);
  }

  temTexto(valor?: string | null): boolean {
    return !!valor?.trim();
  }

  temEndereco(pessoa: IPessoa): boolean {
    const endereco = pessoa.endereco;
    if (!endereco) return false;

    return (
      this.temTexto(endereco.logradouro) ||
      this.temTexto(endereco.numero) ||
      this.temTexto(endereco.complemento) ||
      this.temTexto(endereco.bairro) ||
      this.temTexto(endereco.cidade) ||
      this.temTexto(endereco.estado) ||
      this.temTexto(endereco.codPostal) ||
      this.temTexto(endereco.pais)
    );
  }

  // Actions
  onEditarPerfil() {
    this.editarPerfil.emit();
    if (this.pessoaId) {
      this.router.navigate(['/editar', this.pessoaId]);
    }
  }

  abrirRedefinirSenha(): void {
    if (this.$pessoa()?.contaOAuth) {
      return;
    }
    this.$modalSenhaAberta.set(true);
  }

  fecharRedefinirSenha(): void {
    this.$modalSenhaAberta.set(false);
  }

  onAcessarAjuda() {
    if (!this.pessoaId) return;

    const path = `/minha-conta/${this.pessoaId}/ajuda`;

    if (this.isNomohClienteApp) {
      void this.router.navigateByUrl(path);
      return;
    }

    const clienteBase =
      environment.frontend?.nomohCliente ?? 'http://localhost:4301/';
    window.location.href = `${clienteBase.replace(/\/$/, '')}${path}`;
  }

  onAcessarNomoh() {
    this.acessarNomoh.emit(undefined);
    if (environment.frontend?.nomohAdmin) {
      window.open(environment.frontend.nomohAdmin, '_blank');
    }
  }

  onAcessarBardo() {
    this.acessarBardo.emit(undefined);
    this.abrirUrlBardoAdmin();
  }

  private abrirUrlBardoAdmin(novaAba?: Window | null): void {
    const url = environment.frontend?.bardoAdmin;
    if (!url) return;

    if (novaAba) {
      novaAba.location.href = url;
      novaAba.focus();
      return;
    }

    window.open(url, '_blank');
  }

  async onComprarPlanoBardo() {
    this._loadingBardo.set(true);
    const novaAba = environment.frontend?.bardoAdmin
      ? window.open('', '_blank')
      : null;

    try {
      await this.pessoaDataService.ativarPlanoGratuitoBardo();
      await this.carregarDados();
      this.acessarBardo.emit(undefined);
      this.abrirUrlBardoAdmin(novaAba);
    } catch (error) {
      novaAba?.close();
      throw error;
    } finally {
      this._loadingBardo.set(false);
    }
  }

  async abrirGerenciamentoAssinatura(): Promise<void> {
    this._loadingPortal.set(true);
    try {
      const result = await this.pessoaDataService.abrirPortalAssinatura();
      if (result?.url) {
        window.location.href = result.url;
      }
    } finally {
      this._loadingPortal.set(false);
    }
  }
}
